import React, { useState, useEffect } from "react";
import secureLocalStorage from "react-secure-storage";
import { toSecureUrl } from "./AuthenticatedImage";

// Module-level blob URL cache — persists for the session, cleared on full page reload (logout).
const _blobCache = new Map();

const _getToken = () => {
  try {
    const info = secureLocalStorage.getItem("adminInfo");
    const parsed = typeof info === "string" ? JSON.parse(info) : info;
    return parsed?.authToken || "";
  } catch {
    return "";
  }
};

/**
 * Secure audio player for files stored under /public/ (e.g. chat voice notes).
 *
 * Direct /public/... static access is disabled on the backend — every protected
 * file must be fetched through the authenticated /api/v1/secure-file endpoint.
 * A plain <audio src="/public/..."> can't attach the auth header, so it silently
 * fails to load. This mirrors AuthenticatedImage's blob-fetch strategy instead.
 *
 * blob: URLs (local, not-yet-uploaded recordings) are played back directly since
 * they're already same-origin and need no auth.
 */
const AuthenticatedAudio = ({ src, ...audioProps }) => {
  const [blobUrl, setBlobUrl] = useState(() =>
    src && _blobCache.has(src) ? _blobCache.get(src) : null
  );
  const [useNative, setUseNative] = useState(false);

  useEffect(() => {
    if (!src) return;

    if (src.startsWith("blob:")) {
      setBlobUrl(null);
      setUseNative(true);
      return;
    }

    if (_blobCache.has(src)) {
      setBlobUrl(_blobCache.get(src));
      setUseNative(false);
      return;
    }

    const secureUrl = toSecureUrl(src);
    const token = _getToken();
    let cancelled = false;

    fetch(secureUrl, { headers: { authToken: token } })
      .then((r) => {
        if (!r.ok) throw new Error("not ok");
        return r.blob();
      })
      .then((blob) => {
        if (cancelled) return;
        const url = URL.createObjectURL(blob);
        _blobCache.set(src, url);
        setBlobUrl(url);
      })
      .catch(() => {
        // Secure endpoint unavailable — fall back to native src (e.g. local dev without the route).
        if (!cancelled) setUseNative(true);
      });

    return () => {
      cancelled = true;
    };
  }, [src]);

  if (!src) return null;
  if (blobUrl) return <audio controls src={blobUrl} {...audioProps} />;
  if (useNative) return <audio controls src={src} {...audioProps} />;
  return <audio controls {...audioProps} />;
};

export default AuthenticatedAudio;
